import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { Toast } from 'primereact/toast'

import { PanelContainer } from '../../../../components/Panel'
import { OrderResponseItem } from '../../../../data/models'
import { isButtonDisabled } from '../../../../utils/helper/checkConfirmationCodes'
import { getConfigTranslation } from '../../../../utils/helper/getConfigTranslation'
import {
  isRowSelectable,
  rowClassName,
} from '../../../../utils/helper/isRowSelectable'
import { ConfigurableTable, convertOrderItems } from '../../../../utils/wrapper'
import { useConfigContext } from '../../../ui-config/context'
import { useOrderItems } from '../../data'
import { Edit, EditDate, Info, Reject } from './dialogs'

type Props = {
  id: string
  currency?: string
}

export const Positions: React.FC<Props> = ({ id, currency }) => {
  // const dt = useRef(null)
  const toastSuccess = useRef<Toast | null>(null)
  const { t } = useTranslation()

  const { data: orderResponseItems, isLoading } = useOrderItems(id)

  const [displayEditOrderItems, setDisplayEditOrderItems] = useState(false)

  const [displayInfoOrderItem, setDisplayInfoOrderItem] = useState(false)
  const [orderItem, setOrderItem] = useState()
  const [displayRejectOrderItem, setDisplayRejectOrderItem] = useState(false)
  const [displayEditEditDate, setDisplayEditEditDate] = useState(false)

  const { uiConfig: config } = useConfigContext()

  const [itemId, setItemId] = useState('')

  // dialog for change time and cancel
  const [selectedItems, setSelectedItems] = useState<
    OrderResponseItem[] | null
  >()

  const headerActions = (
    <>
      <Button
        label={t('orderResponse.positions.table.editDeliveryDate.label')}
        onClick={() => setDisplayEditEditDate(true)}
        icon="pi pi-pencil"
        className="mr-2 mb-2"
        disabled={isButtonDisabled('modify', selectedItems)}
        visible={
          config.orderResponse.editOrderItem.confirmationDeliveryDate
            .enabled !== false
        }
        outlined
      />

      <Button
        label={t('orderResponse.positions.table.reject.label')}
        onClick={() => setDisplayRejectOrderItem(true)}
        icon="pi pi-times"
        className="mr-2 mb-2"
        disabled={!selectedItems?.length}
        visible={config.orderResponse.rejectOrderItem.enabled !== false}
        outlined
      />
    </>
  )

  //  action template
  const actionBodyTemplate = (rowData: any) => {
    return (
      <div className="flex justify-content-end flex-nowrap">
        <Button
          icon="pi pi-times"
          className="p-button-rounded p-button-text"
          onClick={() => {
            setDisplayRejectOrderItem(true)
            setSelectedItems([rowData])
          }}
          disabled={rowData.ConfirmationCode === 'REJECTED'}
          visible={config.orderResponse.rejectOrderItem?.enabled !== false}
        />

        <Button
          icon="pi pi-info-circle"
          className="p-button-rounded p-button-text mr-2"
          onClick={() => {
            setOrderItem(rowData)
            setItemId(rowData.Id)
            setDisplayInfoOrderItem(true)
          }}
          visible={config.orderResponse.infoOrderItem.enabled}
        />

        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text"
          onClick={() => {
            setOrderItem(rowData)
            setItemId(rowData.Id)
            setDisplayEditOrderItems(true)
          }}
          visible={config.orderResponse.editOrderItem.enabled}
        />
      </div>
    )
  }

  const orderButtons = (
    <Column key="orderButtons" body={actionBodyTemplate}></Column>
  )

  return (
    <PanelContainer
      header={getConfigTranslation(config.orderResponse.positions.title)}
      id="order-details"
      visible={config.orderResponse.positions.enabled}
    >
      <>
        <ConfigurableTable
          config={config.orderResponse.positions.table}
          data={convertOrderItems(orderResponseItems)}
          buttons={orderButtons}
          headerActions={headerActions}
          checkboxen={true}
          selected={selectedItems}
          setSelected={setSelectedItems}
          isDataSelectable={isRowSelectable}
          rowClassName={rowClassName}
          isLoading={isLoading}
          stateKey="" //"table-order-positions"
        />

        <Toast ref={toastSuccess} position="bottom-right" />

        <EditDate
          visible={displayEditEditDate}
          setVisible={setDisplayEditEditDate}
          items={selectedItems}
          handleItems={setSelectedItems}
          id={id}
        />

        <Reject
          visible={displayRejectOrderItem}
          setVisible={setDisplayRejectOrderItem}
          items={selectedItems}
          handleItems={setSelectedItems}
          id={id}
        />

        {orderItem && (
          <Info
            visible={displayInfoOrderItem}
            setVisible={setDisplayInfoOrderItem}
            id={id}
            itemId={itemId}
            currency={currency!}
          />
        )}

        {orderItem && (
          <Edit
            visible={displayEditOrderItems}
            setVisible={setDisplayEditOrderItems}
            id={id}
            itemId={itemId}
            setDisplayRejectOrderItem={setDisplayRejectOrderItem}
            setSelectedItems={setSelectedItems}
            currency={currency!}
          />
        )}
      </>
    </PanelContainer>
  )
}
